import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { svgSearch } from 'stories/assets'
import { Input, Modal, Tooltip } from 'stories/components'

import { RemovePlaceholderModal } from './RemovePlaceholderModal'
import {
  type IReportHeaders,
  getItemsForField,
  getPlaceholderFromTitle,
  getPlaceholderValue,
  hasPlaceholderPrefix,
} from './types'

export const ReportPlaceholders = ({
  placeholders,
  selectedPlaces,
  setSelectedPlaces,
  type,
  setType,
}: {
  placeholders: Record<string, IReportHeaders[]>
  selectedPlaces: string[]
  setSelectedPlaces: Function
  type: string
  setType: Function
}) => {
  const [query, setQuery] = useState('')
  const [collapseStatus, setCollapseStatus] = useState<Record<string, boolean>>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalItems, setModalItems] = useState<Record<string, any>>({})
  const [modalSelectedItems, setModalSelectedItems] = useState<string[]>([])
  const [modalSelectedValue, setModalSelectedValue] = useState('')
  const [modalTitle, setModalTitle] = useState('')
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [removeModalValue, setRemoveModalValue] = useState('')
  const [removeModalTitle, setRemoveModalTitle] = useState('')

  useEffect(() => {
    setType('Loan')
  }, [])

  useEffect(() => {
    const collapsed: Record<string, boolean> = {}
    placeholders.Loan.forEach((item) => {
      if (hasPlaceholderPrefix(item.title)) collapsed[item.title] = true
    })
    setCollapseStatus(collapsed)
  }, [placeholders])

  const selectedSet = useMemo(() => new Set(selectedPlaces), [selectedPlaces])

  const allPlaceholders = useMemo(() => {
    if (!placeholders[type]) return []
    return placeholders[type].flatMap((item) => item.data.map((v) => getPlaceholderValue(item.title, v)))
  }, [type, placeholders])

  const isAllSelected = useMemo(() => {
    return allPlaceholders.length > 0 && allPlaceholders.every((p) => selectedSet.has(p))
  }, [selectedSet, allPlaceholders])

  const isModalSelectedAll = useMemo(() => {
    const keys = Object.keys(modalItems)
    return keys.length > 0 && keys.length === modalSelectedItems.length
  }, [modalItems, modalSelectedItems])

  const isAllCollapsed = useMemo(() => {
    const sections = placeholders[type] || []
    return sections.length > 0 && sections.every((item) => collapseStatus[item.title])
  }, [collapseStatus, placeholders, type])

  const filteredPlaceholders = useMemo(() => {
    const sections = placeholders[type] || []
    if (!query) return sections
    const lowerQuery = query.toLowerCase()
    return sections.map((header) => ({
      ...header,
      data: header.data
        .filter((v) => header.title.toLowerCase().includes(lowerQuery) || v.toLowerCase().includes(lowerQuery))
        .sort(),
    }))
  }, [type, placeholders, query])

  const onToggleAllCollapse = useCallback(() => {
    const sections = placeholders[type] || []
    const target = !isAllCollapsed
    const newData: Record<string, boolean> = {}
    sections.forEach((item) => {
      newData[item.title] = target
    })
    setCollapseStatus(newData)
  }, [placeholders, type, isAllCollapsed])

  const onToggleAllSelected = useCallback(() => {
    if (isAllSelected) {
      setSelectedPlaces([])
    } else {
      const newData = [...selectedPlaces]
      allPlaceholders.forEach((p) => {
        if (!selectedSet.has(p)) newData.push(p)
      })
      setSelectedPlaces(newData)
    }
  }, [isAllSelected, selectedPlaces, allPlaceholders, selectedSet])

  const onToggleCollapse = useCallback((title: string) => {
    setCollapseStatus((prev) => ({ ...prev, [title]: !prev[title] }))
  }, [])

  const onSelectPlaceholder = useCallback(
    (value: string, title: string, shouldRemove: boolean = false) => {
      const header = getPlaceholderFromTitle(placeholders.Loan, title)
      if (!header) return

      const items = getItemsForField(header, value)
      if (items) {
        setModalSelectedValue(value)
        setModalTitle(header.customTitles?.[value] || value)
        setModalItems(items)
        const prefix = `${value}-`
        setModalSelectedItems(selectedPlaces.filter((key) => key.startsWith(prefix)).map((key) => key.split('-')[1]))
        setIsModalOpen(true)
        return
      }

      const resolved = getPlaceholderValue(title, value)
      if (shouldRemove) {
        const idx = selectedPlaces.indexOf(resolved)
        if (idx !== -1) {
          const next = [...selectedPlaces]
          next.splice(idx, 1)
          setSelectedPlaces(next)
        }
      } else {
        setSelectedPlaces([...selectedPlaces, resolved])
      }
    },
    [placeholders, selectedPlaces],
  )

  const onToggleSectionSelection = useCallback(
    (data: string[], title: string, allSelected: boolean) => {
      const newData = [...selectedPlaces]
      if (allSelected) {
        const toRemove = new Set(data.map((v) => getPlaceholderValue(title, v)))
        setSelectedPlaces(newData.filter((v) => !toRemove.has(v)))
      } else {
        data.forEach((v) => {
          const resolved = getPlaceholderValue(title, v)
          if (!selectedSet.has(resolved)) newData.push(resolved)
        })
        setSelectedPlaces(newData)
      }
    },
    [selectedPlaces, selectedSet],
  )

  const onToggleModalItem = useCallback((value: string) => {
    setModalSelectedItems((prev) => {
      const idx = prev.indexOf(value)
      if (idx === -1) return [...prev, value]
      return prev.filter((_, i) => i !== idx)
    })
  }, [])

  const onModalSubmit = useCallback(() => {
    if (!modalSelectedValue) return
    const prefix = `${modalSelectedValue}-`
    const newKeys = new Set(modalSelectedItems.map((key) => `${prefix}${key}`))
    const filtered = selectedPlaces.filter((key) => !key.startsWith(prefix) || newKeys.has(key))
    const toAdd = [...newKeys].filter((key) => !selectedSet.has(key))
    setSelectedPlaces([...filtered, ...toAdd])
    setModalSelectedItems([])
    setModalSelectedValue('')
    setModalTitle('')
    setModalItems({})
    setIsModalOpen(false)
  }, [modalSelectedValue, modalSelectedItems, selectedPlaces, selectedSet])

  const onRemoveAtIndex = useCallback(
    (index: number) => {
      const next = [...selectedPlaces]
      next.splice(index, 1)
      setSelectedPlaces(next)
      setIsRemoveModalOpen(false)
    },
    [selectedPlaces],
  )

  return (
    <div className="border-r border-gray-200 pr-4">
      <div className="flex gap-4 items-center justify-between">
        <p className="text-lg font-bold mb-4">Placeholders</p>
      </div>

      <div className="flex items-center justify-between mb-2 gap-x-4">
        <div className="-mb-4 flex-1">
          <Input type="search" title="Search" hasIcon icon={svgSearch} value={query} onChange={setQuery} />
        </div>

        <div className="flex items-center gap-3">
          <p className="text-sm cursor-pointer text-shade-blue hover:underline" onClick={onToggleAllCollapse}>
            {isAllCollapsed ? 'Expand All' : 'Collapse All'}
          </p>
          <p className="text-sm cursor-pointer text-shade-blue hover:underline" onClick={onToggleAllSelected}>
            {isAllSelected ? 'Unselect All' : 'Select All'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 text-sm">
        {filteredPlaceholders.map((header, sectionIdx) => {
          const { title, data } = header
          const isOpen = !collapseStatus[title]

          const isSectionAllSelected =
            data.length > 0 && data.every((v) => selectedSet.has(getPlaceholderValue(title, v)))

          return (
            <div key={title} className="col-span-full">
              <p className="flex items-center gap-2 px-2 py-1 bg-gray-100 font-semibold cursor-pointer">
                <span className="flex-1" onClick={() => onToggleCollapse(title)}>
                  {sectionIdx + 1}. {title}
                </span>
                <span
                  className="text-sm cursor-pointer text-shade-blue hover:underline font-normal"
                  onClick={() => onToggleSectionSelection(data, title, isSectionAllSelected)}
                >
                  {isSectionAllSelected ? 'Unselect All' : 'Select All'}
                </span>
                {isOpen ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </p>

              {isOpen && (
                <div className="grid grid-cols-2">
                  {[...new Set(data)].map((v, i) => {
                    const itemsMap = getItemsForField(header, v)
                    const hasItems = !!itemsMap
                    let isSelected = false
                    let tooltip = ''
                    let selectionCount = 0

                    if (hasItems) {
                      const prefix = `${v}-`
                      const matched = selectedPlaces.filter((key) => key.startsWith(prefix))
                      isSelected = matched.length > 0
                      const labels = matched.map((key) => itemsMap[key.split('-')[1]] || key.split('-')[1]).sort()
                      tooltip = labels.reduce((acc, item, idx) => {
                        if (!idx) return item
                        return acc + (idx % 5 === 0 ? ',\n' : ', ') + item
                      }, '')
                    } else {
                      const resolved = getPlaceholderValue(title, v)
                      selectionCount = selectedPlaces.filter((p) => p === resolved).length
                      isSelected = selectionCount > 0
                    }

                    return (
                      <div
                        key={v}
                        className={`flex gap-2 cursor-pointer items-center p-2 border-b border-gray-100 hover:bg-gray-100 ${
                          isSelected ? 'bg-gray-200' : ''
                        }`}
                      >
                        <div
                          className="flex items-center gap-1 flex-1"
                          onClick={() => onSelectPlaceholder(v, title, false)}
                        >
                          <CheckIcon className={`${isSelected ? 'text-shade-blue' : 'text-gray-300'} w-4 h-4`} />
                          <Tooltip message={tooltip}>
                            <p className="text-sm break-all">
                              {i + 1}. {header.customTitles?.[v] || v}
                              {!hasItems && selectionCount > 0 && (
                                <span className="ml-1 text-xs text-shade-blue font-semibold">({selectionCount})</span>
                              )}
                            </p>
                          </Tooltip>
                        </div>
                        {!hasItems && selectionCount > 0 && (
                          <button
                            className="text-xs text-red-600 hover:text-red-800 px-1"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (selectionCount > 1) {
                                setRemoveModalValue(v)
                                setRemoveModalTitle(title)
                                setIsRemoveModalOpen(true)
                              } else {
                                onSelectPlaceholder(v, title, true)
                              }
                            }}
                            title="Remove one instance"
                          >
                            −
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {isModalOpen && (
        <Modal title={`${modalTitle} Details`} isOpen onClose={() => setIsModalOpen(false)} onOk={onModalSubmit}>
          <div>
            <div className="flex justify-end">
              <span
                className="text-sm cursor-pointer text-shade-blue hover:underline font-normal"
                onClick={() => setModalSelectedItems(isModalSelectedAll ? [] : Object.keys(modalItems))}
              >
                {isModalSelectedAll ? 'Unselect All' : 'Select All'}
              </span>
            </div>
            <div className="grid grid-cols-4">
              {Object.entries(modalItems).map(([key, label], index) => {
                const isSelected = modalSelectedItems.includes(key)
                return (
                  <div
                    key={key}
                    className={`flex gap-2 cursor-pointer items-center p-2 border-b border-gray-100 hover:bg-gray-100 ${
                      isSelected ? 'bg-gray-200' : ''
                    }`}
                    onClick={() => onToggleModalItem(key)}
                  >
                    <CheckIcon className={`${isSelected ? 'text-shade-blue' : 'text-gray-300'} w-4 h-4`} />
                    <p className="text-sm">
                      {index + 1}. {label}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </Modal>
      )}

      {isRemoveModalOpen && (
        <RemovePlaceholderModal
          isOpen={isRemoveModalOpen}
          onClose={() => setIsRemoveModalOpen(false)}
          removeModalValue={removeModalValue}
          removeModalTitle={removeModalTitle}
          selectedPlaces={selectedPlaces}
          onRemoveAtIndex={onRemoveAtIndex}
        />
      )}
    </div>
  )
}
